import type { AdminTerminology } from 'types';

import { Role, UserRoleTitle } from 'types';

export const DEFAULT_CLIENT_TERMINOLOGY = Object.freeze({
  plural: 'Clients',
  singular: 'Client',
});

export const ADMIN_TERMINOLOGY_MAX_LENGTH = 80;
// eslint-disable-next-line no-control-regex -- Reject control characters in displayed labels and CSV headers.
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
const FORMULA_PREFIX = /^[=+@-]/;

export function normalizeAdminTerminologyTerm(value: unknown): unknown {
  return typeof value === 'string' && !CONTROL_CHARACTERS.test(value)
    ? value.trim()
    : value;
}

export function isSafeAdminTerminologyTerm(value: string): boolean {
  return !CONTROL_CHARACTERS.test(value) && !FORMULA_PREFIX.test(value.trim());
}

function configuredTerm(value: unknown): string | undefined {
  const normalized = normalizeAdminTerminologyTerm(value);
  return typeof normalized === 'string' &&
    normalized.length > 0 &&
    normalized.length <= ADMIN_TERMINOLOGY_MAX_LENGTH &&
    isSafeAdminTerminologyTerm(normalized)
    ? normalized
    : undefined;
}

export function resolveAdminTerminology(config?: AdminTerminology | null) {
  const configured = config?.client;
  const singular = configuredTerm(configured?.singular);
  const plural = configuredTerm(configured?.plural);
  const client =
    singular && plural ? { plural, singular } : DEFAULT_CLIENT_TERMINOLOGY;
  return {
    client,
    clientId: `${client.singular} ID`,
    roleTitles: {
      ...UserRoleTitle,
      [Role.TENANT_GUEST]: `Organization ${client.singular}`,
    },
  };
}

export type ResolvedAdminTerminology = ReturnType<
  typeof resolveAdminTerminology
>;

// Retain exact legacy messages until older backends and import results without errorCode are retired.
const legacyCodes: Record<string, string> = {
  'Client ID already exists': 'CLIENT_ID_EXISTS',
  'Duplicate Client ID or email in CSV': 'CLIENT_CSV_DUPLICATE',
  'CSV must contain 1–1000 client rows': 'CLIENT_CSV_ROW_COUNT',
  'Invalid Client ID, name, email or phone': 'CLIENT_CSV_INVALID_ROW',
  'Expected exactly one client identifier header': 'CLIENT_CSV_ID_HEADER',
  'Client ID conflicts with an existing Auth0 account':
    'CLIENT_ID_AUTH0_CONFLICT',
  'Expected Client ID,First Name,Last Name,Email Address,Phone headers':
    'CLIENT_CSV_HEADERS',
};

export function formatTerminologyError(
  message: string,
  terminology: ResolvedAdminTerminology,
  code?: string
): string {
  const { client, clientId } = terminology;
  const messages: Record<string, string> = {
    CLIENT_ID_EXISTS: `${clientId} already exists`,
    CLIENT_CSV_DUPLICATE: `Duplicate ${clientId} or email in CSV`,
    CLIENT_CSV_INVALID_ROW: `Invalid ${clientId}, name, email or phone`,
    CLIENT_ID_AUTH0_CONFLICT: `${clientId} conflicts with an existing Auth0 account`,
    CLIENT_CSV_ROW_COUNT: `CSV must contain 1–1000 ${client.singular.toLowerCase()} rows`,
    // Client ID is the permanent legacy CSV alias, even for renamed projects.
    CLIENT_CSV_ID_HEADER: `Expected exactly one identifier column: ${clientId} or Client ID`,
    CLIENT_CSV_HEADERS: `Expected ${clientId},First Name,Last Name,Email Address,Phone headers`,
  };
  return messages[code ?? legacyCodes[message]] ?? message;
}

export function localizeTerminologyError(
  error: unknown,
  terminology: ResolvedAdminTerminology
): string {
  const value = error as {
    message?: unknown;
    data?: { code?: string; message?: unknown };
    response?: { data?: { code?: string; message?: unknown } };
  };
  const data = value?.response?.data ?? value?.data;
  const message = data?.message ?? value?.message ?? 'Something went wrong';
  const messages = Array.isArray(message) ? message : [message];
  return (
    messages
      .filter(
        (item): item is string => typeof item === 'string' && item.length > 0
      )
      .map((item) => formatTerminologyError(item, terminology, data?.code))
      .join('\n') || 'Something went wrong'
  );
}

export function createClientCsvTemplate(terminology: ResolvedAdminTerminology) {
  // Revalidate at the export boundary; a legacy unsafe label must never become a spreadsheet formula.
  const safeTerminology = resolveAdminTerminology({
    client: terminology.client,
  });
  const escapeCsv = (value: string) =>
    /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  // NFKD folds accents (Élève → ELEVE); undecomposable letters (ß, ł, ø) are stripped, not transliterated.
  const idPrefix =
    safeTerminology.client.singular
      .normalize('NFKD')
      .replace(/[^A-Za-z0-9]+/g, '')
      .toUpperCase()
      .slice(0, 32) || 'CLIENT';
  const content = `${escapeCsv(safeTerminology.clientId)},First Name,Last Name,Email Address,Phone\n${idPrefix}001,Test,Participant,${idPrefix.toLocaleLowerCase()}001@example.com,2025550123\n`;
  const stem =
    safeTerminology.client.plural
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'clients';
  return {
    filename: `${stem}-template.csv`,
    url: `data:text/csv;charset=utf-8,${encodeURIComponent(content)}`,
  };
}
